/**
 * Single impact card in the cascade view (P2.6.e).
 *
 * One CascadeImpactOut per card. Shows:
 *   - entity name + kind
 *   - headline finding (the most-severe-most-recent finding in the
 *     group) with severity pill + raised-at + optional source link
 *   - open finding count (rolled-up across the group)
 *   - the 4-chip rollup strip
 *   - a flat "N nodes / M IBSes" cascade summary (real SVG sparkline
 *     lands with TREND in P2.9, backlog #426)
 */
import { listOf } from "shared/i18n/listOf";
import { formatDateTime } from "shared/time/formatDate";
import { entityKindLabelFor } from "shared/vocab/entityKind";
import { i18n } from "shared/i18n";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { isAfterAsOf } from "shared/components/AsOfBanner";

import { FindingLink } from "shared/components/FindingLink";

import { EntityChipStrip } from "twin/components/EntityChipStrip";
import { entityDetailPath } from "shared/routes/entityRoutes";
import type { CascadeImpact, Severity } from "shared/api/types";

const SEVERITY_PILL_CLASSES: Record<Severity, string> = {
  critical: "bg-red-100 text-red-900 border-red-200",
  high: "bg-orange-100 text-orange-900 border-orange-200",
  medium: "bg-amber-100 text-amber-900 border-amber-200",
  low: "bg-slate-100 text-slate-800 border-slate-200",
};

function formatRelative(iso: string): string {
  const raisedAt = new Date(iso);
  if (Number.isNaN(raisedAt.getTime())) {
    return iso;
  }
  return formatDateTime(raisedAt, i18n.language);
}

export interface CascadeImpactCardProps {
  impact: CascadeImpact;
  /** The chosen time, when the reader has rewound the page. */
  asOf?: string | null;
  /** Round 14 G -- a scenario's counterfactual cascade. Its headline is
   *  what the scenario WOULD raise, not a finding anyone raised, so it
   *  is said as that and is not a link. */
  counterfactual?: boolean;
  /** Round 20 P22 -- the severities the cascade counted; when they are
   *  its default (critical and high) the count says so. */
  severities?: readonly string[];
  /** Round 20 U7 -- since when this service has been breaching its
   *  tolerance under live conditions, so a new breach on a service
   *  already red shows. */
  breachingSince?: string | null;
}

export function CascadeImpactCard({
  impact,
  asOf = null,
  counterfactual = false,
  severities,
  breachingSince = null,
}: CascadeImpactCardProps) {
  const { t } = useTranslation();
  const headline = impact.headline_finding;
  // D3 -- the chosen time rewinds the sealed chains, not the findings.
  // A finding raised after it is shown because it is live, not because
  // it was there then, and the card says which.
  const afterAsOf = isAfterAsOf(headline.raised_at, asOf);
  const severity = headline.severity;
  // P272.1 (UX-2 J4-1b): the impacted entity's name links to its detail
  // page -- the walk found the only drill-down path was topnav search.
  const detailPath =
    impact.entity_name != null
      ? entityDetailPath(impact.entity_kind, impact.entity_id)
      : null;

  return (
    <article
      data-testid="cascade-impact-card"
      data-entity-kind={impact.entity_kind}
      data-entity-id={impact.entity_id}
      data-after-as-of={afterAsOf ? "true" : "false"}
      /* ROUND 13 G3 -- a grid item defaults to min-width:auto, so one
         unbreakable child sets the whole track and the page scrolled
         sideways by 48px on a phone. */
      className={`min-w-0 rounded-lg border bg-white p-4 shadow-sm space-y-3 ${
        // Outside the chosen window: muted, so it reads as "not part of
        // what you rewound to" at a glance, with the chip saying why.
        afterAsOf ? "border-slate-200 opacity-70" : "border-slate-200"
      }`}
    >
      <header className="flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-semibold text-slate-900 truncate">
            {detailPath ? (
              <Link
                to={detailPath}
                data-testid="cascade-impact-entity-link"
                className="hover:text-brand-brassDark hover:underline"
              >
                {impact.entity_name}
              </Link>
            ) : (
              (impact.entity_name ?? t("modules.twin.cascade.impact.deleted"))
            )}
          </h3>
          <span className="text-xs uppercase tracking-wide text-slate-500">
            {entityKindLabelFor(impact.entity_kind, impact.service_class)}
          </span>
          {breachingSince && (
            <span
              data-testid="cascade-impact-breaching-since"
              className="ms-2 text-xs font-medium text-rag-red"
            >
              {t("modules.twin.cascade.impact.breachingSince", {
                since: formatDateTime(breachingSince, i18n.language),
              })}
            </span>
          )}
        </div>
        <span className="text-xs text-slate-600 tabular-nums whitespace-nowrap">
          {counterfactual
            ? t("modules.twin.cascade.impact.wouldRaise", {
                count: impact.open_finding_count,
              })
            : t(
                isCriticalAndHigh(severities)
                  ? "modules.twin.cascade.impact.openSerious"
                  : "modules.twin.cascade.impact.openFindings",
                { count: impact.open_finding_count },
              )}
        </span>
      </header>

      <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span
            data-testid="cascade-headline-severity"
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${SEVERITY_PILL_CLASSES[severity]}`}
          >
            {severity}
          </span>
          {counterfactual ? (
            <span
              data-testid="cascade-headline-would-raise"
              className="font-medium text-slate-900 break-words"
            >
              {t("modules.twin.cascade.impact.wouldRaiseTitle", {
                title: headline.title,
              })}
            </span>
          ) : (
            <FindingLink
              id={headline.id}
              title={headline.title}
              className="font-medium text-slate-900 break-words"
            />
          )}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-slate-600">
          {afterAsOf && (
            <span
              data-testid="cascade-headline-after-as-of"
              className="me-1 rounded border border-slate-300 bg-slate-100 px-1 py-0.5 text-[10px] text-slate-500"
            >
              {t("asOfBanner.later")}
            </span>
          )}
          <span data-testid="cascade-headline-raised">
            {counterfactual
              ? t("modules.twin.cascade.impact.modelled")
              : t("modules.twin.cascade.impact.headlineSince", {
                  when: formatRelative(headline.raised_at),
                })}
          </span>
          {!counterfactual && headline.external_url && (
            <a
              href={headline.external_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-brassDark hover:underline"
            >
              {t("modules.twin.cascade.impact.externalLink")}
            </a>
          )}
        </div>
        {/* Round 19 R18 -- a newer critical finding took the headline
            and the entry's open tolerance breach dropped off the card.
            Every open breach on it is linked here, beside the headline. */}
        {!counterfactual && (impact.breach_findings ?? []).length > 0 && (
          <ul
            data-testid="cascade-breach-findings"
            className="mt-2 space-y-0.5 text-xs"
          >
            {(impact.breach_findings ?? []).map((b) => (
              <li key={b.id}>
                <span className="me-1 text-slate-500">
                  {t("modules.twin.cascade.impact.alsoBreached")}
                </span>
                <FindingLink id={b.id} title={b.title} className="text-slate-800" />
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Round 10 F3 -- "IBSES 0" sat beside this card's own sentence
          "Rests on 3 services; nothing downstream depends on it". An
          IBS is the leaf of the blast graph, so that chip is ZERO by
          construction on every IBS card: it is not a measurement, it
          is the answer to a question that does not apply, and the
          sentence below already says it properly. B4 fixed the
          sentence and left the chip printing the same zero. */}
      <EntityChipStrip
        chips={
          impact.entity_kind === "ibs_service"
            ? { ...impact.chips, blast_ibs_count: null }
            : /* Round 18 D25 -- beside "(10 with candidates)" the chip
                 read "IBSES 5": the admitted figure, unlabelled. Round
                 16 D22 -- a channel or facility has no sealed snapshot
                 ("IBSES n/a"). Round 19 P18 -- and a card with no
                 candidate in reach still read "IBSES 1" from the sealed
                 snapshot where every other card read "ADMITTED IBSES".
                 Every card now carries the admitted count, labelled. */
              { ...impact.chips, blast_ibs_count: impact.cascade_ibs_count }
        }
        omitNull={impact.entity_kind === "ibs_service"}
        unadmittedReach={reachesNothingAdmitted(impact)}
        admittedOnly={impact.entity_kind !== "ibs_service"}
      />

      <p
        data-testid="cascade-impact-stats"
        className="text-xs text-slate-500 tabular-nums"
      >
        {/* B4 -- an IBS is the leaf of the blast graph. Services
            support it and nothing depends on it, so this card read
            "reaches 0 IBSes" beneath a banner counting twelve. The
            zero was right and useless; what an IBS can answer is what
            it rests ON. */}
        {impact.entity_kind === "ibs_service"
          ? (impact.funds ?? []).length > 0
            ? t("modules.twin.cascade.impact.restsOnAndFunds", {
                count: impact.upstream_count ?? 0,
                names: listOf((impact.funds ?? []).map((f) => f.name)),
              })
            : t("modules.twin.cascade.impact.restsOn", {
                count: impact.upstream_count ?? 0,
              })
          : /* Round 16 D22 -- "1 nodes in blast · reaches 1 IBSes":
               each number takes its own plural. */
            t("modules.twin.cascade.impact.cascadeStatsCounted", {
              nodes: t("modules.twin.cascade.impact.nodesInBlast", {
                count: impact.cascade_total_nodes,
              }),
              ibses: reachCopy(impact, t),
            })}
      </p>

      {/* P16.2 (#485): customer-journey overlay for impacted IBSes.
          P19.1 (#533): each step rendered as its own chip so the
          inferred failing one can carry a coloured ring + a
          "Failing here" / "Degraded here" badge. */}
      {impact.customer_journeys.length > 0 && (
        <section
          data-testid="cascade-impact-journeys"
          className="border-t border-slate-100 pt-3 space-y-2"
        >
          <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
            {t("modules.twin.cascade.impact.journeysHeading")}
          </h4>
          <ul className="space-y-2">
            {impact.customer_journeys.map((j) => (
              <li
                key={j.journey_id}
                data-testid="cascade-impact-journey"
                data-journey-id={j.journey_id}
                data-failing-step-number={j.failing_step_number ?? ""}
                data-failing-state={j.failing_state ?? ""}
                className="rounded border border-slate-100 bg-slate-50 p-2"
              >
                <p className="text-xs">
                  <span className="font-medium text-slate-900">{j.channel_name}</span>
                  {" · "}
                  <span className="ms-2 text-slate-500 tabular-nums">
                    {t("modules.twin.cascade.impact.journeySteps", {
                      count: j.step_count,
                    })}
                  </span>
                </p>
                {j.step_names.length > 0 && (
                  <ol
                    data-testid="cascade-impact-journey-breadcrumb"
                    className="mt-1 flex flex-wrap items-center gap-1"
                  >
                    {j.step_names.map((name, idx) => {
                      const stepNumber = idx + 1;
                      const isFailing = j.failing_step_number === stepNumber;
                      const tone = !isFailing
                        ? "bg-white ring-1 ring-slate-200 text-slate-700"
                        : j.failing_state === "red"
                          ? "bg-red-50 ring-1 ring-red-300 text-red-800"
                          : "bg-amber-50 ring-1 ring-amber-300 text-amber-800";
                      return (
                        <li
                          key={stepNumber}
                          data-testid="cascade-impact-journey-step"
                          data-step-number={stepNumber}
                          data-failing={isFailing ? "true" : "false"}
                          className={`rounded px-1.5 py-0.5 text-xs ${tone}`}
                        >
                          {name}
                          {isFailing && (
                            <span
                              data-testid="cascade-impact-journey-step-badge"
                              className="ms-1 text-[10px] font-semibold uppercase"
                            >
                              {j.failing_state === "red"
                                ? t("modules.twin.cascade.impact.failingHere")
                                : t("modules.twin.cascade.impact.degradedHere")}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}

/** Round 18 B17 -- does this card stand over nothing admitted? An IBS
 *  card: the IBS is not admitted. Any other card: it reaches no admitted
 *  IBS and no function. Unknown (an older reply) counts as admitted, so
 *  the card reads as before. */
export function reachesNothingAdmitted(impact: CascadeImpact): boolean {
  if (impact.entity_kind === "ibs_service") {
    return impact.admission_status != null && impact.admission_status !== "admitted";
  }
  // Round 19 B28 -- a candidate function is not admitted: on estate D,
  // where nothing was admitted, cards reaching candidate functions were
  // coloured amber and red. The admitted count, where the reply has it.
  const functions =
    impact.cascade_function_admitted_count ?? impact.cascade_function_count ?? 0;
  return impact.cascade_ibs_count === 0 && functions === 0;
}

/** Round 17 P2 -- "reaches 5 admitted IBSes (9 with candidates, 3
 *  critical internal functions)". The war room beside the card judges
 *  all three; a bare "reaches 5" read as a disagreement with it. With
 *  no candidate and no function reached it reads as before. */
function isCriticalAndHigh(severities: readonly string[] | undefined): boolean {
  if (!severities) return false;
  const set = new Set(severities);
  return set.size === 2 && set.has("critical") && set.has("high");
}

function reachCopy(impact: CascadeImpact, t: TFunction): string {
  // Round 20 P20 -- and through how many dependent services.
  const through = impact.cascade_service_count ?? 0;
  const reach = reachCopyBare(impact, t);
  return through > 0
    ? t("modules.twin.cascade.impact.reachesThrough", { reach, count: through })
    : reach;
}

function reachCopyBare(impact: CascadeImpact, t: TFunction): string {
  const admitted = impact.cascade_ibs_count;
  const candidates = impact.cascade_candidate_count ?? 0;
  const functions = impact.cascade_function_count ?? 0;
  if (candidates === 0 && functions === 0) {
    return t("modules.twin.cascade.impact.reachesIbses", { count: admitted });
  }
  const rest: string[] = [];
  if (candidates > 0) {
    rest.push(
      t("modules.twin.cascade.impact.reachesWithCandidates", {
        count: admitted + candidates,
      }),
    );
  }
  if (functions > 0) {
    rest.push(t("modules.twin.cascade.impact.reachesFunctions", { count: functions }));
  }
  return t("modules.twin.cascade.impact.reachesCut", {
    admitted: t("modules.twin.cascade.impact.reachesAdmitted", {
      count: admitted,
    }),
    rest: rest.join(", "),
  });
}
